/**
 * Editor interno de hotspots de la radio.
 *
 * Activación: tecla E o botón [E] EDITAR del header del modal. En modo
 * edición, los 11 hotspots (6 botones + LED + LED-play + 2 reels + knob)
 * reciben outline punteado amarillo y un asa de redimensión en la esquina
 * inferior-derecha. Drag = mover, drag desde la esquina = redimensionar.
 *
 * Una etiqueta flotante muestra coordenadas en vivo del hotspot que se
 * está modificando. Las posiciones se guardan en localStorage al soltar
 * el ratón.
 *
 * Pulsa la tecla E o ESC o el botón HECHO para salir. Botón RESET vuelve
 * todas las posiciones a los defaults.
 *
 * Migración fiel del legacy. Sin cambios funcionales.
 */

import { DEFAULT_HOTSPOTS } from './data.js';
import { applyAllHotspots, getHotspotMap } from './hotspots.js';
import { saveHotspots } from './persistence.js';
import { setEditMode, setHotspots, setPlaying, state } from './state.js';
import { musicAudio } from './audio.js';
import { updatePlayState } from './controls.js';
import type { HotspotId, HotspotRect } from './types.js';

// ─── Estado interno del editor ───────────────────────────────────────────

interface ActiveDrag {
  key: HotspotId;
  mode: 'move' | 'resize';
  startX: number;
  startY: number;
  /** Rect original al iniciar el drag, para calcular deltas. */
  orig: HotspotRect;
}

let activeDrag: ActiveDrag | null = null;
let coordsLabel: HTMLDivElement | null = null;
let listenersBound = false;

// ─── Activación / desactivación ──────────────────────────────────────────

/**
 * Toggle del modo edición. Maneja el estado, la clase CSS del modal,
 * el banner, el botón del header y la pausa del audio.
 */
export function toggleRadioEditMode(): void {
  setEditMode(!state.editMode);
  const modal = document.getElementById('r-modal');
  const banner = document.getElementById('r-edit-banner');
  const btn = document.getElementById('r-edit-btn');

  if (state.editMode) {
    modal?.classList.add('r-edit-mode');
    if (banner) banner.style.display = 'flex';
    btn?.classList.add('r-active');
    // Pausar audio durante edición para no entorpecer
    if (state.isPlaying) {
      musicAudio.pause();
      setPlaying(false);
      updatePlayState();
    }
    bindEditorListeners();
  } else {
    modal?.classList.remove('r-edit-mode');
    if (banner) banner.style.display = 'none';
    btn?.classList.remove('r-active');
    removeCoordsLabel();
  }
}

// ─── Listeners de drag ───────────────────────────────────────────────────

/** Idempotente: registra los listeners de drag una sola vez. */
function bindEditorListeners(): void {
  if (listenersBound) return;
  listenersBound = true;

  // Listener por hotspot: mousedown inicia el drag
  for (const [elId, key] of getHotspotMap()) {
    const el = document.getElementById(elId);
    if (!el) continue;
    el.addEventListener('mousedown', (e: MouseEvent) => {
      if (!state.editMode) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = el.getBoundingClientRect();
      const isResize = e.clientX > rect.right - 12 && e.clientY > rect.bottom - 12;
      activeDrag = {
        key,
        mode: isResize ? 'resize' : 'move',
        startX: e.clientX,
        startY: e.clientY,
        orig: { ...state.hotspots[key] },
      };
      showCoordsLabel();
    });
  }

  // Listener global de mousemove: actualiza posición/tamaño durante drag
  document.addEventListener('mousemove', (e: MouseEvent) => {
    if (!activeDrag || !state.editMode) return;
    const wrap = document.querySelector<HTMLElement>('.r-radio-wrap');
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const dx = ((e.clientX - activeDrag.startX) / rect.width) * 100;
    const dy = ((e.clientY - activeDrag.startY) / rect.height) * 100;
    const h = state.hotspots[activeDrag.key];
    const o = activeDrag.orig;

    if (activeDrag.mode === 'move') {
      h.left = Math.max(0, Math.min(100, o.left + dx));
      h.top = Math.max(0, Math.min(100, o.top + dy));
    } else {
      h.width = Math.max(0.5, Math.min(100, o.width + dx));
      h.height = Math.max(0.5, Math.min(100, o.height + dy));
    }
    applyAllHotspots();
    updateCoordsLabel(e);
  });

  // Mouseup: finaliza drag y persiste
  document.addEventListener('mouseup', () => {
    if (activeDrag) {
      activeDrag = null;
      saveHotspots();
      removeCoordsLabel();
    }
  });
}

// ─── Etiqueta flotante de coordenadas ────────────────────────────────────

function showCoordsLabel(): void {
  if (coordsLabel) return;
  coordsLabel = document.createElement('div');
  coordsLabel.className = 'r-edit-coords';
  document.body.appendChild(coordsLabel);
}

function updateCoordsLabel(e: MouseEvent): void {
  if (!coordsLabel || !activeDrag) return;
  const h = state.hotspots[activeDrag.key];
  coordsLabel.textContent =
    `${activeDrag.key.toUpperCase()}: ` +
    `${h.left.toFixed(1)}% / ${h.top.toFixed(1)}% — ` +
    `${h.width.toFixed(1)}×${h.height.toFixed(1)}%`;
  coordsLabel.style.left = e.clientX + 14 + 'px';
  coordsLabel.style.top = e.clientY + 14 + 'px';
}

function removeCoordsLabel(): void {
  if (coordsLabel) {
    coordsLabel.remove();
    coordsLabel = null;
  }
}

// ─── Reset ───────────────────────────────────────────────────────────────

/**
 * Restablece todos los hotspots a sus posiciones por defecto. Pide
 * confirmación antes para evitar borrados accidentales.
 */
export function resetHotspots(): void {
  if (!confirm('¿Restablecer las posiciones de los hotspots a los valores por defecto?')) return;
  setHotspots(structuredClone(DEFAULT_HOTSPOTS));
  saveHotspots();
  applyAllHotspots();
}

// ─── Init ────────────────────────────────────────────────────────────────

/**
 * Conecta los listeners de los botones del banner del editor (RESET y
 * HECHO). Llamar una sola vez desde init.
 */
export function initRadioEditor(): void {
  const resetBtn = document.getElementById('r-edit-reset');
  const doneBtn = document.getElementById('r-edit-done');

  if (resetBtn && resetBtn.dataset.bound !== '1') {
    resetBtn.dataset.bound = '1';
    resetBtn.addEventListener('click', resetHotspots);
  }
  if (doneBtn && doneBtn.dataset.bound !== '1') {
    doneBtn.dataset.bound = '1';
    doneBtn.addEventListener('click', () => {
      if (state.editMode) toggleRadioEditMode();
    });
  }
}
